/**
 * Марка площадки: цветной знак вместо строчки серого текста.
 *
 * Зачем. В момент ставки человек держит открытыми две чужие вкладки и наш
 * экран, и первый вопрос к плитке — «это какая из двух?». Название, набранное
 * тем же кеглем и тем же цветом, что и всё вокруг, на этот вопрос отвечает
 * чтением; цветной знак — узнаванием. Владелец 08.08: «хотел бы логотипы
 * видеть, чтобы было интуитивно понятно».
 *
 * Картинки появились 12.08 и решили ровно ту задачу, ради которой марка
 * заводилась: контору узнают знаком быстрее, чем читают названием. Возражение
 * против картинок было верным для чужого файла из сети — но знаки лежат у нас в
 * `public/venues`, весят по шесть килобайт, приведены к одному квадрату и едут в
 * том же релизе, что и код. Сети в момент ставки они не трогают.
 *
 * Словесная марка никуда не делась и остаётся ответом на незнакомую контору:
 * зеркало приносит коды, которых мы заранее не знаем, а пустое место рядом с
 * названием читалось бы как «эта площадка хуже».
 *
 * Цвета — украшение, и ошибка в оттенке ничего не стоит. Единственное, что
 * обязано работать всегда, — РАЗЛИЧИМОСТЬ двух ног, поэтому неизвестной
 * площадке цвет считается из её кода: зеркальных контор мы заранее не знаем
 * (`meta.venues` их не называет), а серая плитка рядом с цветной читалась бы как
 * «эта хуже».
 *
 * Имя и знак не живут внутри `<a href=контора>`. 13.08 и 17.08 это съедало
 * строку ленты (`:visited`, глобальный `a { color }`, блокировщик ссылок на БК).
 * 23.08 то же повторилось в раскрытой карточке: первая плитка пустела, вторая
 * (Vbet) оставалась — фильтр режет `bc.game` и оставляет соседа. Пустой хит
 * поверх тоже прятали. Вкладку открываем сами, с названия, не со знака:
 * 01.09 тестер жал в точку и уезжал на биржу. Как в `Row.tsx`.
 *
 * Правый клик копирует тот же адрес, левый открывает. Из РФ половина вкладок
 * не грузится («нельзя в вашем регионе» или крутится пустая страница) — ссылку
 * кладут в профиль с прокси. Имя в `<a>` ради «копировать адрес» не возвращать.
 */

import { FEED_KIND } from '../lib/feedContext';
import { useCopyFlash } from './Grab';
import { logoOf } from './venues';

/**
 * Известные марки: один фирменный тон на контору. Пусто — цвет из кода (`hued`).
 *
 * Тон, а не пара «фон и текст». Заливка знака берётся из тона с прозрачностью,
 * а буквы — тем же тоном посветлее: две плотные плашки рядом (жёлтая и синяя)
 * на тёмной карточке дерутся друг с другом и с суммой, ради которой карточка
 * существует, — 08.08, владелец: «вырвиглазная».
 */
const BRANDS: Record<string, { tone: string; text?: string }> = {
  bet365: { tone: '#1a9d76', text: 'bet365' },
  melbet: { tone: '#f5c518', text: 'Melbet' },
  fonbet: { tone: '#2bb573', text: 'Фонбет' },
  winline: { tone: '#e8564e', text: 'Winline' },
  leon: { tone: '#f0a01e', text: 'Леон' },
  olimp: { tone: '#4a8fe0', text: 'Олимп' },
  zenit: { tone: '#6f9bd8', text: 'Зенит' },
  betboom: { tone: '#ff7a3d', text: 'BetBoom' },
  betcity: { tone: '#e8c34a', text: 'Betcity' },
  pari: { tone: '#ff6a4d', text: 'PARI' },
  marathon: { tone: '#e06a75', text: 'Marathon' },
  parimatch: { tone: '#e8e04a', text: 'Parimatch' },
  onexbet: { tone: '#5b9be8', text: '1xБит' },
  onexbetru: { tone: '#5b9be8', text: '1xbet' },
  '1xbet': { tone: '#5b9be8', text: '1xbet' },
  '1win': { tone: '#9b5cff', text: 'Mellstroy' },
  onewin: { tone: '#9b5cff', text: 'Mellstroy' },
  '888sport': { tone: '#e8813d', text: '888sport' },
  '888starz': { tone: '#e8a03d', text: '888starz' },
  mostbet: { tone: '#4fb3e8', text: 'Mostbet' },
  ggbet: { tone: '#ff8a4a', text: 'GGBet' },
  // Тон — заливка знака (корона на #019cee), не мятный плейсхолдер.
  jetton: { tone: '#019cee', text: 'JetTon' },
  fortunejack: { tone: '#019cee', text: 'JetTon' },
  vodkabet: { tone: '#4ec3e0', text: 'Vodka Bet' },
  riobet: { tone: '#e31c23', text: 'RioBet' },
  // Тон — акцент витрины duel.com (`#e1b23f`). Знак — белая «D» на чёрном
  // (`#dark-icon` из favicon.svg), не wordmark и не скрещенные мечи duelbits.
  duel: { tone: '#e1b23f', text: 'Duel' },
  pinnacle: { tone: '#e0555f', text: 'Pinnacle' },
  orbitex: { tone: '#1f8a4c', text: 'Orbitex' },
  betfair: { tone: '#1f8a4c', text: 'Orbitex' },
  dafabet: { tone: '#c41e3a', text: 'Dafabet' },
  dafa: { tone: '#c41e3a', text: 'Dafabet' },
  egb: { tone: '#ff7a18', text: 'EGB' },
  bxyz: { tone: '#7b6cff', text: 'Betxyz' },
  betxyz: { tone: '#7b6cff', text: 'Betxyz' },
  freewin: { tone: '#2bb36a', text: 'Freewin' },
  sharpxch: { tone: '#4a7fd4', text: 'Sharp Exchange' },
  buff: { tone: '#2ad4c8', text: 'Buffbet' },
  buffbet: { tone: '#2ad4c8', text: 'Buffbet' },
  dexsport: { tone: '#2ec8c0', text: 'Dexsport' },
  polymarket: { tone: '#5b82f0', text: 'Polymarket' },
  kalshi: { tone: '#33d3aa', text: 'Kalshi' },
  limitless: { tone: '#9a86ff', text: 'Limitless' },
  predictfun: { tone: '#ff6b7d', text: 'Predict.fun' },
  sxbet: { tone: '#4fc0ff', text: 'SX Bet' },
  thunderpick: { tone: '#9070ff', text: 'Thunderpick' },
  cloudbet: { tone: '#33cfa0', text: 'Cloudbet' },
  bcgame: { tone: '#e8c14a', text: 'BC.Game' },
  bovada: { tone: '#e06a75', text: 'Bovada' },
  csgopositive: { tone: '#ff9a4a', text: 'CSGOPositive' },
  // Вывеска Mellstroy, код источника `welstroy` — протокол и адаптер не трогать.
  welstroy: { tone: '#9b5cff', text: 'Mellstroy' },
  mellstroy: { tone: '#9b5cff', text: 'Mellstroy' },
};

/** Код площадки к виду ключа: зеркало пишет `Bet365`, `bet_365`, `BET365`. */
const key = (code: string): string => code.toLowerCase().replace(/[^a-zа-я0-9]/gi, '');

/**
 * Цвет незнакомой площадки — из её кода.
 *
 * Тон стабилен: одна контора не имеет права менять цвет между кадрами, иначе
 * узнавание, ради которого знак и заводился, работает против нас. Светлота и
 * насыщенность фиксированы, поэтому чёрный текст читается на любом тоне.
 */
function hued(code: string): string {
  let hash = 0;
  for (const ch of code) hash = (hash * 31 + ch.codePointAt(0)!) % 360;
  // Насыщенность низкая нарочно: незнакомых контор в зеркальной ленте больше,
  // чем знакомых, и неоновое пятно на каждой второй плитке — это не узнавание, а
  // резь в глазах.
  return `oklch(0.75 0.1 ${hash})`;
}

export function Brand({
  code,
  name,
  size = 'm',
  href,
  onOpen,
}: {
  code: string;
  name: string;
  /** `s` — в строку рядом с текстом, `m` — знак в шапке плитки. */
  size?: 's' | 'm';
  /** Адрес конторы. В разметку как `href` не кладётся — см. шапку файла. */
  href?: string | null;
  /** Открыть вкладку самим. Без него, но с `href`, зовём `window.open`. */
  onOpen?: () => void;
}): React.ReactElement {
  const known = (FEED_KIND === 'main' || FEED_KIND === 'tests') && (key(code) === '1win' || key(code) === 'onewin')
    ? { tone: '#ff4d1a', text: '1win' } : BRANDS[key(code)];
  const tone = known?.tone ?? hued(code);
  const logo = logoOf(code);
  const title = known?.text ?? name;
  const go = onOpen !== undefined || Boolean(href);
  const { took, fail, flash, take } = useCopyFlash();
  const open = (event: { preventDefault(): void; stopPropagation(): void }): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onOpen !== undefined) {
      onOpen();
      return;
    }
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };
  const hint = took
    ? 'ссылка в буфере'
    : fail
      ? 'не скопировалось'
      : href
        ? `${name} · правый клик — скопировать ссылку`
        : name;
  const hit = go
    ? {
        role: 'link' as const,
        tabIndex: 0,
        'data-href': href ?? undefined,
        onClick: (event: React.MouseEvent) => open(event),
        onPointerDown: (event: React.PointerEvent) => event.stopPropagation(),
        onContextMenu: (event: React.MouseEvent) => {
          if (!href) return;
          event.preventDefault();
          event.stopPropagation();
          take(href);
        },
        onAuxClick: (event: React.MouseEvent) => {
          // Средняя — открыть, как «в новой вкладке». Правая раньше тоже
          // открывала (`auxclick` ловит обе), и из РФ человек терял адрес.
          if (event.button === 1) {
            open(event);
            return;
          }
          event.preventDefault();
          event.stopPropagation();
        },
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          open(event);
        },
      }
    : {};
  const nameHit = (
    <b className={`nx-brand__name${flash}`} title={hint} {...hit}>
      {title}
    </b>
  );
  if (logo !== null) {
    // Со знаком цветная плашка лишняя: она добавляла узнавание, которого не
    // давало серое название, а теперь узнавание даёт сам знак. Название рядом
    // остаётся текстом — его копируют и ищут поиском. Хит только на слове:
    // знак раскрывает карточку, не вкладку.
    return (
      <span className={`nx-brand nx-brand--logo nx-brand--${size}`}>
        <img src={logo} alt="" loading="lazy" decoding="async" />
        {nameHit}
      </span>
    );
  }
  return (
    <span
      className={`nx-brand nx-brand--${size}`}
      // Тон один, а работ у него две: буквы им же и подложка из него же на
      // 14 %. Формой знак остаётся спокойным, цветом — узнаваемым.
      style={{
        color: tone,
        background: `color-mix(in oklch, ${tone} 14%, transparent)`,
        borderColor: `color-mix(in oklch, ${tone} 34%, transparent)`,
      }}
    >
      {nameHit}
    </span>
  );
}
